/** @format */

import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { MdOutlineKeyboardArrowRight } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import FadeLoader from "react-spinners/FadeLoader";
import api from "../api/api";
import AddressManager from "../components/AddressManager";
import Footer from "../components/Footer";
import Headers from "../components/Headers";
import { get_customer_addresses } from "../store/reducers/addressReducer";
import { place_order } from "../store/reducers/orderReducer";
import { useCategories } from "../utils/Categories";
import { devise } from "../utils/utils";

const Shipping = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { t } = useTranslation();

  // const { state: { products, price: initialPrice, Shipping_fees: initialShippingFees, items } } = useLocation();
  // Sécurisation de la récupération du state (évite un crash au refresh)
  const location = useLocation();
  const {
    products = [],
    price: initialPrice = 0,
    Shipping_fees: initialShippingFees = 0,
    items = 0,
    mode = "cart",
  } = location?.state ?? {};

  const { customerInfo } = useSelector((state) => state.auth);
  const { customerAddresses } = useSelector((state) => state.address);

  const [selectedAddress, setSelectedAddress] = useState(null);
  const [shippingFees, setShippingFees] = useState(initialShippingFees);
  const [orderPrice, setOrderPrice] = useState(initialPrice);
  const [isRecalculating, setIsRecalculating] = useState(false);

  let total = parseFloat(orderPrice) + parseFloat(shippingFees);

  // ✅ Recalculer les frais de livraison lors de la sélection d’une adresse
  /*
    const handleRecalculateDeliveryFees = async () => {
        if (!selectedAddress) return

     console.log('selected products', products)

        setIsRecalculating(true)
        try {
            const response = await api.post("/api/cart/recalculate", {
                customerId: customerInfo.id,
                address: {
                    country: selectedAddress.country,
                    city: selectedAddress.city,
                    region: selectedAddress.region,
                    postalCode: selectedAddress.postalCode,
                    address: selectedAddress.address
                }
            })

           // console.log('response ', response)

            const { price, shipping_fee } = response.data
            setShippingFees(shipping_fee)
            setOrderPrice(price)
            toast.success("Frais de livraison recalculés")
        } catch (error) {
            console.error("Erreur recalcul frais:", error)
            toast.error("Impossible de recalculer les frais de livraison")
        } finally {
            setIsRecalculating(false)
        }
    }
*/

  // normalise l'addresse
  const normalizeAddress = (addr) => {
    if (!addr) return null;
    return {
      country: addr.country,
      city: addr.city,
      // certains jeux de données utilisent 'area' au lieu de 'region'
      area: addr.area ?? addr.region ?? "",
      region: addr.region ?? addr.area ?? "",
      postalCode: addr.postalCode,
      address: addr.address,
      phone: addr.phone,
      name: addr.name,
    };
  };

  // ✅ Placer la commande
  /*
    const placeOrderHandler = () => {
        if (!customerInfo) {
            toast("Vous n'êtes pas connecté")
            return
        }

        dispatch(place_order({
            products,
            price: parseFloat(orderPrice),
            Shipping_fees: parseFloat(shippingFees),
            items,
            shippingInfo: selectedAddress,
            customerId: customerInfo.id,
            navigate
        }))
    }
*/
  const placeOrderHandler = () => {
    if (!customerInfo) {
      toast(`${t("You are not connected")}`);
      return;
    }

    dispatch(
      place_order({
        products, // snapshot des items (BUY NOW ou panier)
        price: parseFloat(orderPrice),
        Shipping_fees: parseFloat(shippingFees),
        items,
        shippingInfo: normalizeAddress(selectedAddress),
        customerId: customerInfo.id,
        mode, // 'buynow' ou 'cart' (si tu veux traiter différemment côté back)
        navigate,
      }),
    );
  };

  // ✅ Charger les adresses du client
  useEffect(() => {
    if (customerInfo) {
      dispatch(get_customer_addresses(customerInfo.id));
    }
  }, [customerInfo, dispatch]);

  useEffect(() => {
    const getDeliveryType = () => {
      if (mode === "buynow" && products.length > 0) {
        const firstGroup = products[0];
        const firstProduct = firstGroup.products[0];
        return firstProduct?.productInfo?.deliveryType || "dynamic";
      }
      return null;
    };

    const handleRecalculateDeliveryFees = async () => {
      if (!selectedAddress) return;

      setIsRecalculating(true);
      try {
        const payload = {
          customerId: customerInfo.id,
          address: normalizeAddress(selectedAddress),
        };

        // Si on vient de BUY NOW, on passe les produits “inline” pour éviter d’utiliser le panier en BDD
        if (
          mode === "buynow" &&
          Array.isArray(products) &&
          products.length > 0
        ) {
          payload.inlineProducts = products; // <— important
        }

        const response = await api.post("/api/cart/recalculate", payload);

        const { price, shipping_fee } = response.data || {};
        if (typeof price === "number") setOrderPrice(price);
        if (typeof shipping_fee === "number") setShippingFees(shipping_fee);

        toast.success(`${t("Calculating delivery fees")}`);
      } catch (error) {
        console.error("Erreur recalcul frais:", error);
        toast.error(`${t("Impossible to calculate delivery fees")}`);
      } finally {
        setIsRecalculating(false);
      }
    };

    if (!selectedAddress) return;

    const deliveryType = getDeliveryType();

    // ✅ Cas BUY NOW
    if (mode === "buynow") {
      if (deliveryType === "dynamic") {
        // seulement si dynamic → appel API
        handleRecalculateDeliveryFees();
      } else {
        // sinon on ne touche pas aux frais (déjà calculés côté ProduitsDetails.jsx)
        console.log(`Skip recalcul: mode=buynow, deliveryType=${deliveryType}`);
      }
    }

    // ✅ Cas PANIER classique → toujours recalcul
    if (mode === "cart") {
      handleRecalculateDeliveryFees();
    }
  }, [selectedAddress, mode, products, customerInfo.id, t]);

  const { categories, loading, errorMessage, clientLocation } = useCategories();

  return (
    <div>
      <Headers categories={categories} clientLocation={clientLocation} />
      {loading ? (
        <div className='w-screen h-screen flex justify-center items-center fixed left-0 top-0 bg-gray-500/70 z-[99999]'>
          <FadeLoader />
        </div>
      ) : (
        ""
      )}

      {errorMessage ? <div>Error: {errorMessage}</div> : ""}
      <section
        style={{
          backgroundImage: `url('${process.env.REACT_APP_CLIENT_URL}/images/card/1.jpg')`,
        }}
        className='h-[220px] sm:h-[80px] mt-6 bg-no-repeat relative bg-left bg-cover select-none'
      >
        <div className='absolute left-0 top-0 w-full h-full bg-blue-800/30'>
          <div className='w-[80%] md:w-[80%] sm:w-[90%] lg:w-[90%] h-full mx-auto '>
            <div className='flex flex-col justify-center gap-1 items-center h-full w-full text-white'>
              <h1 className='text-3xl font-bold rounded-full'>
                {t("My Shop")}
              </h1>
              <div className='flex gap-2 items-center justify-center text-2xl w-full '>
                <Link to='/' className='rounded-full'>
                  {t("Home")}
                </Link>
                <span className='pt-2'>
                  <MdOutlineKeyboardArrowRight />
                </span>
                <span>{t("Place Order")}</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className='bg-[#eeeeed]'>
        <div className='w-[85%] lg:w-[90%] md:w-[90%] sm:w-[90%] mx-auto py-8'>
          <div className='w-full flex flex-wrap'>
            <div className='w-[67%] md-lg:w-full'>
              <div className='flex flex-col gap-3'>
                <div className='bg-white p-6 shadow-sm rounded-md'>
                  <AddressManager
                    onSelect={setSelectedAddress}
                    customerInfo={customerInfo}
                    customerAddresses={customerAddresses}
                  />
                </div>
                {products.map((card, cardIndex) => (
                  <div
                    key={cardIndex}
                    className='bg-white flex flex-col gap-4 p-4'
                  >
                    <div className='flex justify-start items-center'>
                      <h2 className='text-md font-bold text-slate-600'>
                        {card.shopName}
                      </h2>
                    </div>
                    {card.products.map((product, index) => {
                      const variant = product.selectedVariant;
                      //const displayImage = variant?.variantImage || product.productInfo.images[0]?.url || product.productInfo.images[0];

                      const displayImage =
                        typeof variant?.variantImage === "string"
                          ? variant.variantImage
                          : (variant?.variantImage?.url ??
                            product.productInfo.images?.[0]?.url ??
                            product.productInfo.images?.[0]);
                      /*
                                            const displayPrice = variant
                                                ? (variant.variantPrice * (1 - (product.productInfo.discount || 0) / 100)).toFixed(2)
                                                : (product.productInfo.price * (1 - (product.productInfo.discount || 0) / 100)).toFixed(2);
                                                */

                      const stock = variant
                        ? variant.variantStock
                        : product.productInfo.stock;

                      return (
                        <div
                          key={index}
                          className='flex flex-wrap gap-2 border-b pb-2 mb-2'
                        >
                          <Link
                            to={`/product/details/${product.productInfo?._id}`}
                          >
                            <img
                              src={displayImage}
                              alt={product.productInfo.name}
                              className='h-[80px] w-auto object-contain'
                            />
                          </Link>
                          <div className='flex-1'>
                            <h2 className='text-sm font-semibold uppercase'>
                              {product.productInfo.name}{" "}
                              {variant && (
                                <span className='text-blue-600'>
                                  [{variant.color} {variant.size}]
                                </span>
                              )}
                            </h2>
                            <span className='text-sm'>
                              {product.productInfo.brand}
                            </span>
                            <p>Stock: {stock}</p>
                            <p>
                              {t("Delivery")}:{" "}
                              {product.productInfo.estimatedDeliveryTime}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
            <div className='w-[33%] md-lg:w-full'>
              <div className='pl-3 md-lg:pl-0'>
                <div className='bg-white p-5 font-medium flex flex-col gap-3 rounded-md shadow-md'>
                  <h2 className='text-xl font-semibold'>
                    {t("Order Summary")}
                  </h2>
                  <div className='flex justify-between items-center'>
                    <span>{t("Total Items")}</span>
                    <span>{items}</span>
                  </div>
                  <div className='flex justify-between items-center'>
                    <span>{t("Delivery Fees")}</span>
                    <span>
                      {shippingFees} {devise}
                    </span>
                  </div>
                  <div className='flex justify-between items-center'>
                    <span>{t("Total payment")}</span>
                    <span>
                      {orderPrice} {devise}
                    </span>
                  </div>
                  <div className='flex justify-between items-center text-green-600'>
                    <span>Total</span>
                    <span>
                      {total} {devise}
                    </span>
                  </div>
                  <button
                    onClick={placeOrderHandler}
                    disabled={!selectedAddress || isRecalculating}
                    className={`w-full bg-green-600 text-white py-3 rounded mt-5 ${!selectedAddress || isRecalculating ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {isRecalculating
                      ? `${t("Is calculating...")}`
                      : `${t("Order")}`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Shipping;
